// ============================================================================
// L'ASSISTANT DE CRÉATION DE COMPTE — un compte CLIENT (gérant, vendeur…) ou un compte ORGAZONE (notre équipe).
//
// UTILITÉ : un seul parcours pour inviter une personne, que la maquette ouvre depuis le tableau de bord (« Créer un
// utilisateur »), l'écran des identités, la fiche d'un client et la fin de la création d'un magasin.
// ⚠️ CE QUE CHAQUE PARCOURS ÉCRIT, ET RIEN D'AUTRE :
//    · Orgazone : le compte et son rôle Console (`/staff`) — l'invitation part de l'IdP ;
//    · Client : l'identité chez l'IdP rattachée au client, PUIS le compte dans le BACK-OFFICE des magasins cochés, avec
//      un rôle que le back-office applique (Administrateur, Opérateur). Sans ce second geste la personne s'authentifie
//      et le back-office la refuse (« compte non provisionné »).
// ⚠️ ÉCARTS ASSUMÉS AVEC LA MAQUETTE (`apps/console/CONVENTIONS-PRODUCTION.md`) : les rôles d'un client sont ceux du
//    back-office, pas les modèles « Rôles et droits » de la Console — que rien n'applique encore ; les modules et accès
//    délégués d'un compte Orgazone sont grisés « à venir » — aucune application ne les lit (décisions de l'utilisateur).
// ============================================================================
import { useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { alert, Icon, useOverlayPanel } from '@orgazone/ui-web';
import { api, type IdpAccountRow, type TenantAccessOptions, type TenantRow } from '../api';
import { useAuth, can } from '../auth';
import { STAFF_DELEGATIONS, STAFF_MODULES, STAFF_ROLES, STAFF_ROLE, type StaffRole } from '../staff-roles';

/**
 * L'adresse proposée à l'invitation : le contact principal saisi à la création du client, s'il y en a un.
 * ⚠️ Une PROPOSITION, jamais une décision : le champ reste modifiable, et l'écran dit d'où vient l'adresse.
 */
export function inviteDefaultEmail(contactEmail: string | null | undefined): string {
  return (contactEmail ?? '').trim();
}

export type AccountType = 'client' | 'staff';
export type BoRole = 'admin' | 'operator';

/** Ouverture depuis un client connu (sa fiche, la fin d'une mise en service) : le rattachement n'est pas resaisi. */
export interface WizardPreset {
  tenantId: string;
  tenantName: string;
  /** Les magasins cochés d'office — à défaut, tous ceux du client. */
  glns?: string[];
  /** Le contact principal du client : proposé comme adresse (décision de l'utilisateur). */
  contactEmail?: string | null;
  /** Le rôle proposé — l'administrateur à la fin d'une mise en service. */
  boRole?: BoRole;
}

/** Les deux rôles qu'un back-office pose, décrits d'après ses permissions (`boRolePermissions`). */
export const BO_ROLES: { v: BoRole; label: string; desc: string }[] = [
  { v: 'admin', label: 'Administrateur', desc: 'Tout le back-office des magasins cochés : il le modifie, ouvre les accès de ses collaborateurs et consulte le poste de conformité NF525.' },
  { v: 'operator', label: 'Opérateur', desc: 'Consulte le back-office des magasins cochés, sans rien y modifier.' },
];

/** Prénom ou nom réduit à ses lettres — sert à juger qu'il est saisi et à composer la variante d'une adresse. */
export const slug = (v: string): string => v.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z]/g, '');

/** La forme d'une adresse, sans prétendre la valider — seul l'envoi réel le prouvera. */
export const emailShapeOk = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[a-z]{2,}$/i.test(email.trim());

/** Le compte qui porte déjà cette adresse (comparée sans casse ni blancs), ou `null`. */
export function takenBy(email: string, accounts: readonly IdpAccountRow[] | null): IdpAccountRow | null {
  const e = email.trim().toLowerCase();
  return e && accounts ? accounts.find((a) => a.email.toLowerCase() === e) ?? null : null;
}

/** La variante sous-adressée d'une adresse prise : `contact+celine@enseigne.fr` — la messagerie livre au même endroit. */
export function aliasFor(email: string, firstName: string): string {
  const e = email.trim();
  const at = e.indexOf('@');
  return at > 0 ? `${e.slice(0, at)}+${slug(firstName) || 'prenom'}${e.slice(at)}` : '';
}

/** Les étapes du parcours — un client mono-magasin choisit seulement son rôle, un multi-magasin ses magasins aussi. */
export function wizardSteps(type: AccountType | null, multiStore: boolean): string[] {
  if (type === 'staff') return ['Type de compte', 'Rôle Orgazone', 'Récapitulatif'];
  if (type === 'client') return ['Type de compte', 'Client de rattachement', multiStore ? 'Magasins et rôle' : 'Rôle', 'Récapitulatif'];
  return ['Type de compte', 'Rattachement', 'Accès', 'Récapitulatif'];
}

/** L'étape suivante — ouvert depuis un client connu, le rattachement est sauté (et l'est aussi au retour). */
export const nextStep = (step: number, type: AccountType | null, preset: boolean): number =>
  step + (step === 0 && preset && type === 'client' ? 2 : 1);
export const prevStep = (step: number, type: AccountType | null, preset: boolean): number =>
  step - (step === 2 && preset && type === 'client' ? 2 : 1);

/** La durée du lien d'invitation, lue chez l'IdP — `null` quand on ne la connaît pas (on ne l'invente pas). */
export function inviteTtlText(ms: number | null | undefined): string | null {
  if (!ms || ms <= 0) return null;
  const h = Math.round(ms / 3_600_000);
  if (h >= 48 && h % 24 === 0) return `Le lien d’invitation est valable ${h / 24} jours.`;
  return `Le lien d’invitation est valable ${h} heure${h > 1 ? 's' : ''}.`;
}

/** Message d'invitation honnête : parti, déjà actif, ou compte prêt sans e-mail (transport à régler). */
export function inviteMessage(r: { inviteSent?: boolean; alreadyActive?: boolean }, email: string): string {
  if (r.alreadyActive) return `${email} a déjà un accès IdP actif — la personne peut se connecter directement.`;
  if (r.inviteSent) return `Invitation envoyée à ${email}.`;
  return `Compte prêt, mais l'e-mail d'invitation n'est pas parti (transport e-mail de l'IdP à configurer) — renvoyez-le une fois l'e-mail actif.`;
}

const overlaySt: CSSProperties = {
  position: 'fixed', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: 24, background: 'color-mix(in srgb, var(--canvas) 68%, transparent)', backdropFilter: 'blur(14px) saturate(1.4)',
};
const boxSt: CSSProperties = {
  width: 840, maxWidth: '100%', maxHeight: 'min(880px, calc(100vh - 48px))', display: 'flex', flexDirection: 'column',
  background: 'color-mix(in srgb, var(--elev) 92%, transparent)', backdropFilter: 'var(--glass-blur)',
  border: '1px solid var(--ink200)', borderRadius: 'var(--r-lg)', boxShadow: '0 30px 70px -24px rgba(0,0,0,.7)', overflow: 'hidden',
};
const eyebrowSt: CSSProperties = { fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' };
const labelSt: CSSProperties = { display: 'block', fontSize: 'var(--t-dense)', fontWeight: 600, color: 'var(--ink600)', marginBottom: 5 };
const inputSt: CSSProperties = { width: '100%', height: 40, padding: '0 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--canvas)', color: 'var(--ink900)', fontSize: 'var(--t-control)', outline: 'none' };
const noteSt: CSSProperties = { display: 'flex', gap: 9, padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--ink200)', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink600)', lineHeight: 1.55 };
const warnSt: CSSProperties = { marginTop: 10, padding: '11px 13px', borderRadius: 'var(--r-sm)', border: '1px solid var(--wa-fg)', background: 'var(--wa-bg)', color: 'var(--wa-fg)', fontSize: 'var(--t-note)', lineHeight: 1.5 };

/** Une tuile de choix (type de compte), cochée ou non. */
function Tile({ on, disabled, onClick, icon, title, desc, foot, reason }: {
  on: boolean; disabled: boolean; onClick: () => void; icon: ReactNode; title: string; desc: string; foot: string; reason: string | null;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'flex-start', textAlign: 'left', padding: 18,
        borderRadius: 'var(--r-md)', border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink200)'}`,
        background: on ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      }}>
      <span style={{ width: 42, height: 42, borderRadius: 12, display: 'grid', placeItems: 'center', background: on ? 'var(--ok-bg)' : 'var(--ink100)', color: on ? 'var(--ok-fg)' : 'var(--ink600)' }}>{icon}</span>
      <span style={{ fontSize: 'var(--t-title)', fontWeight: 700, marginTop: 12 }}>{title}</span>
      <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5, marginTop: 6 }}>{desc}</span>
      <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink400)', marginTop: 10 }}>{foot}</span>
      {reason && <span style={{ fontSize: 'var(--t-note)', fontWeight: 600, color: 'var(--wa-fg)', marginTop: 8 }}>{reason}</span>}
    </button>
  );
}

/** Une carte de rôle à cocher (rond), avec sa description et ce qu'elle permet. */
function RoleCard({ on, onClick, title, desc, children, tip }: { on: boolean; onClick: () => void; title: string; desc: string; children?: ReactNode; tip?: string }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={on} title={tip}
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', padding: '13px 15px', borderRadius: 'var(--r-sm)', textAlign: 'left',
        border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink200)'}`, background: on ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)', cursor: 'pointer',
      }}>
      <span style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, marginTop: 3, border: on ? '5px solid var(--teal-500)' : '1.5px solid var(--ink300)' }} />
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-title)', fontWeight: 700 }}>{title}</span>
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5, marginTop: 4 }}>{desc}</span>
        {children}
      </span>
    </button>
  );
}

/** Une puce à cocher (magasin), cochée ou non. */
function Chip({ on, onClick, disabled, children, tip }: { on: boolean; onClick?: () => void; disabled?: boolean; children: ReactNode; tip?: string }) {
  return (
    <button type="button" onClick={onClick} disabled={disabled} aria-pressed={on} title={tip}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8, height: 34, padding: '0 12px', borderRadius: 9, fontSize: 'var(--t-dense)', fontWeight: 600,
        border: `1px solid ${on ? 'var(--teal-500)' : 'var(--ink200)'}`, background: on ? 'var(--ok-bg)' : 'transparent',
        color: on ? 'var(--ok-fg)' : 'var(--ink600)', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1,
      }}>
      <span aria-hidden style={{ width: 16, height: 16, borderRadius: 5, display: 'grid', placeItems: 'center', fontSize: 'var(--t-micro)', border: '1px solid var(--ink300)', background: on ? 'var(--teal-500)' : 'transparent', color: 'var(--canvas)' }}>{on ? '✓' : ''}</span>
      {children}
    </button>
  );
}

export function AccountWizard({ preset, onClose, onDone }: { preset?: WizardPreset; onClose: () => void; onDone: (notice: string) => void }) {
  const { user } = useAuth();
  const canClient = can(user, 'console.account.write');
  const canStaff = can(user, 'console.staff.write');

  const [step, setStep] = useState(0);
  const [type, setType] = useState<AccountType | null>(preset ? 'client' : null);
  const [first, setFirst] = useState('');
  const [last, setLast] = useState('');
  const presetEmail = inviteDefaultEmail(preset?.contactEmail);
  const [email, setEmail] = useState(presetEmail);
  /** L'adresse injectée depuis le contact du client — tant qu'elle n'est pas modifiée, l'écran dit d'où elle vient. */
  const [proposed, setProposed] = useState(presetEmail);
  const [staffRole, setStaffRole] = useState<StaffRole>('support_ops'); // moindre privilège — jamais super-admin d'office
  const [tenantId, setTenantId] = useState<string | null>(preset?.tenantId ?? null);
  const [tenantName, setTenantName] = useState<string>(preset?.tenantName ?? '');
  const [query, setQuery] = useState('');
  const [glns, setGlns] = useState<string[]>(preset?.glns ?? []);
  const [boRole, setBoRole] = useState<BoRole>(preset?.boRole ?? 'operator');
  const [busy, setBusy] = useState(false);
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR — sauf pendant l'envoi, que fermer laisserait sans
  //    témoin. Ouvert PAR-DESSUS la mise en service, l'assistant est la dernière couche : Échap est à lui seul.
  const { panelRef } = useOverlayPanel({ open: true, onClose, blockClose: () => busy });
  const [err, setErr] = useState<string | null>(null);

  const [accounts, setAccounts] = useState<IdpAccountRow[] | null>(null);
  const [accountsErr, setAccountsErr] = useState<string | null>(null);
  const [tenants, setTenants] = useState<TenantRow[] | null>(null);
  const [tenantsErr, setTenantsErr] = useState<string | null>(null);
  const [options, setOptions] = useState<TenantAccessOptions | null>(null);
  const [optionsErr, setOptionsErr] = useState<string | null>(null);
  const [contact, setContact] = useState<string | null>(presetEmail || null);
  const [inviteTtl, setInviteTtl] = useState<number | null>(null);

  // Les identités existantes : c'est elles qui disent « cette adresse a déjà un compte ».
  useEffect(() => {
    api.identityAccounts().then((r) => setAccounts(r.accounts)).catch((e) => setAccountsErr((e as Error).message));
    api.linkSettings().then((r) => setInviteTtl(r.idp?.inviteTtlMs ?? null)).catch(() => setInviteTtl(null));
  }, []);
  // Ouvert sur un client sans son contact (fin d'une mise en service sur un client existant) : on le lit, et il ne
  // remplit l'adresse que si rien n'y est encore tapé.
  // ⚠️ Lecture sans échec affiché, et c'est voulu : le contact n'est qu'une PROPOSITION — sans lui, le champ reste
  //    vide et rien n'est annoncé, exactement comme pour un client qui n'en a pas.
  const presetTenantId = preset?.tenantId ?? null;
  const contactGiven = preset?.contactEmail !== undefined;
  useEffect(() => {
    if (!presetTenantId || contactGiven) return;
    let alive = true;
    api.tenant(presetTenantId).then((d) => {
      const c = inviteDefaultEmail(d.tenant.contact?.email);
      if (!alive || !c) return;
      setContact(c);
      setEmail((cur) => (cur.trim() ? cur : c));
      setProposed(c);
    }).catch(() => undefined);
    return () => { alive = false; };
  }, [presetTenantId, contactGiven]);
  // La liste des clients ne se charge que pour un compte client à rattacher (pas depuis la fiche d'un client).
  useEffect(() => {
    if (type !== 'client' || preset || tenants) return;
    // ⚠️ UNE RÉPONSE DÉPASSÉE N'ÉCRIT PAS : basculer de type pendant la lecture puis revenir à « client » en lance une
    //    seconde — la première, revenue après, réécrirait la liste ou poserait un échec que l'écran courant n'a pas eu.
    let alive = true;
    api.tenants()
      .then((r) => { if (alive) setTenants(r.tenants); })
      .catch((e) => { if (alive) setTenantsErr((e as Error).message); });
    return () => { alive = false; };
  }, [type, preset, tenants]);
  // Les magasins et la licence du client choisi.
  useEffect(() => {
    if (!tenantId) { setOptions(null); return; }
    let alive = true;
    setOptions(null); setOptionsErr(null);
    api.tenantAccessOptions(tenantId).then((o) => {
      if (!alive) return;
      setOptions(o);
      // ⚠️ Sans présélection, tous les magasins du client sont cochés (maquette) : on décoche ce qui ne doit pas s'ouvrir.
      setGlns((cur) => (cur.length ? cur.filter((g) => o.stores.some((s) => s.gln === g)) : o.stores.map((s) => s.gln)));
    }).catch((e) => { if (alive) setOptionsErr((e as Error).message); });
    return () => { alive = false; };
  }, [tenantId]);

  const stores = options?.stores ?? [];
  const multi = stores.length > 1;
  const steps = wizardSteps(type, multi);
  const taken = takenBy(email, accounts);
  const emailOk = !!type && slug(first).length > 1 && slug(last).length > 1 && emailShapeOk(email) && !taken;
  const fromContact = proposed !== '' && email.trim() === proposed;
  const isRecap = step === steps.length - 1 && type !== null;

  const canNext = step === 0
    ? emailOk && (type === 'client' ? canClient : canStaff)
    : type === 'client' && step === 1 ? !!tenantId
      : type === 'client' && step === 2 ? glns.length > 0
        : true;
  const footNote = step === 0 && !emailOk ? 'Prénom, nom et adresse valide requis.'
    : step === 1 && type === 'client' && !tenantId ? 'Sélectionnez le client de rattachement.'
      : step === 2 && type === 'client' && options && stores.length > 0 && glns.length === 0 ? 'Cochez au moins un magasin.'
        : '';

  const pickTenant = (t: TenantRow) => {
    setTenantId(t.id); setTenantName(t.name); setGlns([]); setContact(null);
    // Le contact principal du client — proposé comme adresse quand il diffère de celle saisie.
    api.tenant(t.id).then((d) => setContact(inviteDefaultEmail(d.tenant.contact?.email) || null)).catch(() => setContact(null));
  };

  const submit = async () => {
    setErr(null); setBusy(true);
    const mail = email.trim();
    try {
      if (type === 'staff') {
        const r = await api.createStaff({ firstName: first.trim(), lastName: last.trim(), email: mail, role: staffRole });
        onDone(`Compte Orgazone créé — ${inviteMessage(r, mail)}`);
        return;
      }
      const inv = await api.inviteTenantAccount(tenantId!, { firstName: first.trim(), lastName: last.trim(), email: mail });
      try {
        const bo = await api.openBoAccess(tenantId!, mail, { role: boRole, glns });
        const label = BO_ROLES.find((r) => r.v === boRole)!.label;
        onDone(`${inviteMessage(inv, mail)} Back-office ouvert (${label}${bo.created ? '' : ', compte existant complété'}).`);
      } catch (e) {
        // ⚠️ L'IDENTITÉ EXISTE, LE BACK-OFFICE NE S'EST PAS OUVERT : le dire tel quel. Taire le second échec ferait
        //    attendre une connexion que le back-office refusera ; le relancer se fait depuis la fiche du client.
        await alert({
          title: 'Back-office non ouvert',
          message: `${inviteMessage(inv, mail)} Mais le back-office ne s’est pas ouvert : ${(e as Error).message}. Ouvrez-le depuis la fiche du client, onglet Utilisateurs.`,
        });
        onDone(`Compte ${mail} créé — back-office NON ouvert.`);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (!canNext || busy) return;
    if (isRecap) { void submit(); return; }
    setStep(nextStep(step, type, !!preset));
  };
  const back = () => (step > 0 ? setStep(prevStep(step, type, !!preset)) : onClose());

  const crumbs = ['Nouveau compte', ...(type ? [type === 'staff' ? 'Orgazone' : 'Client'] : [])];
  const desc = [
    "Choisissez d'abord le type de compte : le reste du parcours s'y adapte. L'adresse sert d'identifiant de connexion.",
    type === 'staff' ? 'Un seul rôle Orgazone par compte. Il détermine ce que la personne voit de tous les clients.' : 'Le client détermine les magasins que ce compte pourra ouvrir.',
    type === 'staff' ? "Vérifiez avant d'envoyer : un compte Orgazone voit plusieurs clients." : (multi ? 'Les magasins autorisés, puis le rôle dans leur back-office.' : 'Le rôle détermine ce que la personne voit et peut faire dans le back-office.'),
    "Vérifiez avant l'envoi.",
  ][step] ?? '';
  const filteredTenants = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (tenants ?? []).filter((t) => !q || `${t.name} ${t.businessKind ?? ''}`.toLowerCase().includes(q));
  }, [tenants, query]);
  const nextLabel = !type ? 'Choisissez un type' : isRecap ? (busy ? 'Envoi…' : "Envoyer l'invitation") : 'Continuer →';
  const ttl = inviteTtlText(inviteTtl);

  // ⚠️ PAR UN PORTAIL : ouvert depuis une autre fenêtre (la mise en service), un ancêtre à flou d'arrière-plan
  //    confinerait sa couche « fixe » à la boîte de cet ancêtre au lieu de l'écran.
  return createPortal(
    <div style={overlaySt}>
      <div ref={panelRef} style={boxSt} role="dialog" aria-modal="true" aria-label="Nouveau compte">
        <div style={{ padding: '18px 24px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                {crumbs.map((c, i) => (
                  <span key={c} style={{ display: 'inline-flex', gap: 7, fontSize: 'var(--t-dense)', fontWeight: i === crumbs.length - 1 && i > 0 ? 700 : 500, color: i === crumbs.length - 1 && i > 0 ? 'var(--ink900)' : 'var(--ink500)' }}>
                    {i > 0 && <span style={{ color: 'var(--ink400)' }}>›</span>}{c}
                  </span>
                ))}
              </div>
              <h3 style={{ margin: '7px 0 0', fontSize: 18, fontWeight: 700, letterSpacing: '-.02em' }}>{steps[step]}</h3>
              <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 4, lineHeight: 1.5, maxWidth: 620 }}>{desc}</div>
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div className="mono" style={eyebrowSt}>étape</div>
              <div data-step-count style={{ fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-.03em', lineHeight: 1.15 }}>
                {step + 1}<span style={{ fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink400)' }}> / {type ? steps.length : '3 ou 4'}</span>
              </div>
            </div>
            <button type="button" className="btn-icon" onClick={onClose} aria-label="Fermer" style={{ width: 30, height: 30, flexShrink: 0 }}>✕</button>
          </div>
          <div style={{ height: 3, borderRadius: 3, background: 'var(--ink200)', marginTop: 16, overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: 3, background: 'var(--teal-500)', width: `${type ? Math.round(((step + 1) / steps.length) * 100) : 8}%`, transition: 'width .2s ease' }} />
          </div>
        </div>

        <div style={{ padding: '20px 24px 24px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
          {step === 0 && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                <Tile on={type === 'client'} disabled={!canClient || (!!preset && type !== 'client')} onClick={() => setType('client')}
                  icon={<Icon name="users" size={24} />}
                  title="Compte client" desc="Un gérant, un vendeur, un responsable réseau. Rattaché à un client, il n'accède qu'à ses magasins."
                  foot="realm CLIENTS · 2FA recommandée" reason={canClient ? null : 'Réservé au super-admin.'} />
                <Tile on={type === 'staff'} disabled={!canStaff || !!preset} onClick={() => { setType('staff'); setTenantId(null); }}
                  icon={<Icon name="lock" size={24} />}
                  title="Compte Orgazone" desc="Notre équipe : super-admin, support, exploitation. Voit plusieurs clients, jamais leurs données de vente."
                  foot="realm STAFF · 2FA obligatoire" reason={canStaff ? null : 'Réservé au super-admin.'} />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 14, marginTop: 18 }}>
                <div>
                  <label htmlFor="uw-first" style={labelSt}>Prénom</label>
                  <input id="uw-first" value={first} onChange={(e) => setFirst(e.target.value)} placeholder="Céline" style={inputSt} />
                </div>
                <div>
                  <label htmlFor="uw-last" style={labelSt}>Nom</label>
                  <input id="uw-last" value={last} onChange={(e) => setLast(e.target.value)} placeholder="Vireau" style={inputSt} />
                </div>
              </div>
              <div style={{ marginTop: 14 }}>
                <label htmlFor="uw-email" style={labelSt}>Adresse e-mail (identifiant de connexion)</label>
                <input id="uw-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mono"
                  placeholder={type === 'staff' ? 'prenom.nom@orgazone.fr' : 'prenom.nom@enseigne.fr'}
                  style={{ ...inputSt, borderColor: taken ? 'var(--wa-fg)' : 'var(--ink200)' }} />
                {fromContact && <div data-email-source style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 5 }}>Reprise du contact principal de « {preset?.tenantName} » — modifiable.</div>}
                {taken ? (
                  <div role="alert" style={warnSt}>
                    <div style={{ fontWeight: 700 }}>Cette adresse a déjà un compte</div>
                    <div style={{ marginTop: 5 }}>
                      Un compte = une personne : {taken.name ?? taken.email}{taken.tenantName ? ` (${taken.tenantName})` : taken.realm === 'staff' ? ' (Orgazone)' : ''} utilise déjà cet identifiant.
                      Pour une seconde personne sur la même boîte, ajoutez son prénom après un « + » — la messagerie livre les deux au même endroit. Exemple à adapter :
                    </div>
                    {aliasFor(email, first) && (
                      <button type="button" className="mono" onClick={() => setEmail(aliasFor(email, first))} data-alias
                        style={{ marginTop: 9, height: 28, padding: '0 10px', borderRadius: 8, border: '1px solid var(--ink200)', background: 'var(--paper)', color: 'var(--ink900)', fontSize: 'var(--t-sub)', fontWeight: 600 }}>
                        {aliasFor(email, first)}
                      </button>
                    )}
                  </div>
                ) : type === 'staff' && email.trim() && !email.trim().toLowerCase().endsWith('@orgazone.fr') ? (
                  <div style={warnSt}>
                    <div style={{ fontWeight: 700 }}>Adresse hors domaine orgazone.fr</div>
                    <div style={{ marginTop: 5 }}>Un compte Orgazone se crée normalement sur le domaine de l'éditeur. Continuez seulement si c'est délibéré (prestataire, astreinte externe).</div>
                  </div>
                ) : type === 'client' ? (
                  <div style={{ ...noteSt, marginTop: 10 }}>Deux gérants, une seule boîte ? Le sous-adressage crée deux identités distinctes livrées au même endroit : contact+celine@enseigne.fr. La messagerie ignore le « + » — à tester si l'hébergeur est ancien.</div>
                ) : null}
                {/* ⚠️ Sans la liste des identités, « adresse libre » ne se prouve pas : le dire, l'IdP refusera un doublon. */}
                {accountsErr && <div style={warnSt}>Vérification des adresses existantes indisponible : {accountsErr}. Un doublon sera refusé à l'envoi.</div>}
              </div>
            </>
          )}

          {step === 1 && type === 'staff' && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                {STAFF_ROLES.map((r) => (
                  <RoleCard key={r.v} on={staffRole === r.v} onClick={() => setStaffRole(r.v)} title={r.label} desc={r.desc} tip={`Permission technique : ${r.scope}`}>
                    <span style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                      {r.can.map((c) => <span key={c} style={{ fontSize: 'var(--t-sub)', padding: '2px 7px', borderRadius: 6, background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>{c}</span>)}
                      {r.cannot.map((c) => <span key={c} style={{ fontSize: 'var(--t-sub)', padding: '2px 7px', borderRadius: 6, background: 'var(--ink100)', color: 'var(--ink500)', textDecoration: 'line-through' }}>{c}</span>)}
                    </span>
                  </RoleCard>
                ))}
              </div>
              {/* ⚠️ GRISÉS « À VENIR » (décision de l'utilisateur) : aucune application ne lit encore ces accès. */}
              <div className="mono" style={{ ...eyebrowSt, margin: '20px 0 9px' }}>modules ajoutés au rôle · à venir</div>
              <div data-staff-modules style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {STAFF_MODULES.map((m) => <Chip key={m.k} on={false} disabled tip={`Permission technique : ${m.scope}`}>{m.name}</Chip>)}
              </div>
              <div className="mono" style={{ ...eyebrowSt, margin: '20px 0 9px' }}>accès délégué aux outils des clients · à venir</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                {STAFF_DELEGATIONS.map((d) => <Chip key={d.k} on={false} disabled tip={`Permission technique : ${d.scope}`}>{d.name}</Chip>)}
              </div>
              <div style={{ ...noteSt, marginTop: 10 }}>
                À venir : aucune application ne lit encore ces accès — l'EDI et le store privé ouvrent l'accès automatiquement en bêta, et l'accès au back-office d'un magasin passe par « Ouvrir le back-office du magasin » sur sa fiche. Rien n'est donc posé ici.
              </div>
              <div style={{ ...warnSt, marginTop: 16 }}>
                Trois verbes restent réservés au super-admin quel que soit le rôle : <b>supprimer une base</b>, <b>copier une DSN</b>, <b>créer un compte</b>. La 2FA est imposée dès la première connexion.
              </div>
            </>
          )}

          {step === 1 && type === 'client' && (
            <>
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Nom du client, métier…" aria-label="Rechercher un client"
                style={{ ...inputSt, maxWidth: 420, height: 36 }} />
              <div style={{ marginTop: 12, border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden', maxHeight: 330, overflowY: 'auto' }}>
                {tenantsErr ? <div className="err" style={{ margin: 12 }}>Clients indisponibles : {tenantsErr}</div>
                  : !tenants ? <div className="muted" style={{ padding: 14 }}>Chargement des clients…</div>
                    : filteredTenants.length === 0 ? (
                      <div style={{ padding: '30px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <span aria-hidden="true" style={{ width: 50, height: 50, borderRadius: 16, display: 'grid', placeItems: 'center', background: 'var(--ink100)', color: 'var(--ink400)', marginBottom: 13 }}>
                          <Icon name="zoomOut" size={25} />
                        </span>
                        <div style={{ fontSize: 'var(--t-body)', fontWeight: 700 }}>Aucun client ne correspond</div>
                        <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink500)', marginTop: 6 }}>Vérifiez l'orthographe, ou créez d'abord le client depuis « Créer un client ou un magasin ».</div>
                      </div>
                    ) : filteredTenants.map((t) => (
                      <button key={t.id} type="button" onClick={() => pickTenant(t)} aria-pressed={tenantId === t.id}
                        style={{ display: 'flex', alignItems: 'flex-start', gap: 11, width: '100%', padding: '11px 14px', border: 'none', borderBottom: '1px solid var(--ink100)', background: tenantId === t.id ? 'var(--ok-bg)' : 'transparent', color: 'var(--ink900)', cursor: 'pointer', textAlign: 'left' }}>
                        <span style={{ width: 15, height: 15, borderRadius: '50%', flexShrink: 0, marginTop: 2, border: tenantId === t.id ? '5px solid var(--teal-500)' : '1.5px solid var(--ink300)' }} />
                        <span style={{ minWidth: 0, flex: 1 }}>
                          <span style={{ fontSize: 'var(--t-body)', fontWeight: 600 }}>{t.name}</span>
                          {t.businessKind && <span style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginLeft: 9 }}>{t.businessKind}</span>}
                          <span className="mono" style={{ display: 'block', fontSize: 'var(--t-sub)', color: 'var(--ink500)', marginTop: 3 }}>{t.storeCount} magasin{t.storeCount > 1 ? 's' : ''}</span>
                        </span>
                      </button>
                    ))}
              </div>
              {/* La règle de l'utilisateur : le contact principal du client est proposé — un clic, jamais imposé. */}
              {tenantId && contact && contact !== email.trim() && (
                <div data-contact-suggestion style={{ ...noteSt, marginTop: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span style={{ flex: 1, minWidth: 200 }}>Contact principal de « {tenantName} » : <span className="mono">{contact}</span></span>
                  {takenBy(contact, accounts)
                    ? <span style={{ color: 'var(--wa-fg)', fontWeight: 600 }}>déjà un compte</span>
                    : <button type="button" className="btn-ghost sm" onClick={() => setEmail(contact)}>Utiliser cette adresse</button>}
                </div>
              )}
            </>
          )}

          {step === 2 && type === 'client' && (
            <>
              {preset && <div style={{ ...noteSt, marginBottom: 14, background: 'var(--ok-bg)', color: 'var(--ok-fg)' }}>Rattachement repris : {preset.tenantName} — inutile de le resaisir.</div>}
              {optionsErr ? <div className="err">Magasins indisponibles : {optionsErr}</div>
                : !options ? <div className="muted">Chargement des magasins…</div>
                  // ⚠️ AUCUNE CONDITION DE LICENCE : elle est commerciale, jamais un verrou d'usage (route `…/bo-access`).
                  : stores.length === 0 ? (
                    <div role="alert" style={warnSt}>Ce client n'a encore aucun magasin : il n'y a rien à ouvrir.</div>
                  ) : (
                    <>
                      {multi ? (
                        <>
                          <div className="mono" style={{ ...eyebrowSt, marginBottom: 9 }}>périmètre — magasins autorisés</div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                            {stores.map((s) => {
                              const on = glns.includes(s.gln);
                              return (
                                <Chip key={s.gln} on={on} onClick={() => setGlns(on ? glns.filter((g) => g !== s.gln) : [...glns, s.gln])}>
                                  <span>{s.name}</span><span className="mono" style={{ fontSize: 'var(--t-sub)', opacity: 0.7 }}>{s.gln}</span>
                                </Chip>
                              );
                            })}
                          </div>
                        </>
                      ) : (
                        <div style={{ ...noteSt, background: 'var(--ink-50)' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: 'var(--ink900)' }}>{stores[0]!.name} · <span className="mono">{stores[0]!.gln}</span></div>
                            <div style={{ marginTop: 3 }}>Client mono-magasin : le périmètre est ce magasin — choisissez le rôle ci-dessous.</div>
                          </div>
                        </div>
                      )}
                      <div className="mono" style={{ ...eyebrowSt, margin: '20px 0 9px' }}>rôle dans le back-office</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                        {BO_ROLES.map((r) => <RoleCard key={r.v} on={boRole === r.v} onClick={() => setBoRole(r.v)} title={r.label} desc={r.desc} />)}
                      </div>
                      <div style={{ ...noteSt, marginTop: 14 }}>Ce sont les rôles que le back-office applique. Les « Rôles et droits » de la Console ne s'y appliquent pas encore.</div>
                    </>
                  )}
            </>
          )}

          {isRecap && (
            <>
              <div data-recap style={{ border: '1px solid var(--ink200)', borderRadius: 'var(--r-sm)', overflow: 'hidden' }}>
                {([
                  ['Type de compte', type === 'staff' ? 'Orgazone (staff)' : 'Client'],
                  ['Personne', `${first.trim()} ${last.trim()}`.trim()],
                  ['Identifiant', email.trim()],
                  ...(type === 'staff'
                    ? [['Rôle', STAFF_ROLE[staffRole].label], ['Modules', 'à venir — aucune application ne les lit encore'], ['Accès délégué', 'à venir — aucune application ne les lit encore']]
                    : [['Client', tenantName], ['Périmètre', stores.filter((s) => glns.includes(s.gln)).map((s) => s.name).join(', ')], ['Rôle', `${BO_ROLES.find((r) => r.v === boRole)!.label} · Back-office`]]),
                  ['2FA', type === 'staff' ? 'obligatoire à la première connexion' : 'recommandée, non imposée'],
                ] as [string, string][]).map(([k, v]) => (
                  <div key={k} style={{ display: 'flex', gap: 14, padding: '10px 15px', borderBottom: '1px solid var(--ink100)' }}>
                    <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', width: 150, flexShrink: 0 }}>{k}</span>
                    <span className={k === 'Identifiant' ? 'mono' : undefined} style={{ fontSize: 'var(--t-dense)', fontWeight: 600, overflowWrap: 'anywhere' }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ ...noteSt, marginTop: 14 }}>
                L'e-mail de bienvenue commencera par « Bonjour {first.trim() || '…'} ».{ttl ? ` ${ttl}` : ''} L'utilisateur pose lui-même son mot de passe : aucun mot de passe n'est saisi ici, et personne chez nous ne le connaît.
              </div>
              {err && <div className="err" style={{ marginTop: 12 }}>{err}</div>}
            </>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 24px', borderTop: '1px solid var(--ink200)', flexShrink: 0, flexWrap: 'wrap' }}>
          <button type="button" className="btn-ghost" onClick={back} disabled={busy}>← Retour</button>
          <div style={{ flex: 1 }} />
          <span style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', maxWidth: 340, lineHeight: 1.45 }}>{footNote}</span>
          <button type="button" className="btn-cta" onClick={next} disabled={!canNext || busy}
            style={canNext ? undefined : { background: 'var(--ink200)', color: 'var(--ink400)', cursor: 'not-allowed' }}>
            {nextLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
